import { Injectable, Provider } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HTTP_INTERCEPTORS } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { AppSettingsLoaderService } from '@core/services/common/app-settings-loader.service';
import { ArkikStatusService } from '@core/services/common/arkik-status.service';

@Injectable()
export class OnlineInterceptor implements HttpInterceptor {
  constructor(
    private readonly _router: Router,
    private readonly _appSettings: AppSettingsLoaderService,
    public arkikStatus: ArkikStatusService,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!environment.isLocalWeb) return next.handle(request);

    const keys = Object.keys(localStorage);
    const tokenKey: string = keys.find((x) =>
      x.includes(environment.B2C_tenantName + '.b2clogin.com' + '-accesstoken'),
    );
    const whiteList: string[] = ['/public', '/EdgeSettings'];
    const isUrlWhileListed: boolean = whiteList.some((item) => request.url.includes(item));
    const apim: string = this._appSettings?.getConfig()?.onlineApiBase ?? environment.urlApimOnline;
    const isArkikOnline = this.arkikStatus.isArkikOnline();
    const isThereComponentOnline = environment.componentsOnline && environment.componentsOnline.length > 0;
    const isThereUrlOnline = environment.urlsOnline && environment.urlsOnline.length > 0;
    let isOnlineRequired = false;

    //Validate if the request is for a component that requires online connection
    if (environment.isLocalWeb && isArkikOnline && tokenKey && !isUrlWhileListed && isThereComponentOnline) {
      const connectionOnline = this.isRequiredConnectionOnlineByComponent();
      if (connectionOnline) {
        let newUrl = request.url.replace(environment.apiManagmet, apim);
        newUrl = this.handleUrlExceptions(newUrl);
        request = request.clone({
          url: newUrl,
        });
        isOnlineRequired = true;
      }
    }

    //Validate if request is for a url that requires online connection
    if (environment.isLocalWeb && isArkikOnline && tokenKey && !isUrlWhileListed && isThereUrlOnline) {
      const urls = environment.urlsAlwaysOnline + environment.urlsOnline;
      const urlsOnl: string[] = urls.split(',');
      for (const url of urlsOnl) {
        if (request.url.includes(url)) {
          let newUrl = request.url.replace(environment.apiManagmet, apim);
          newUrl = this.handleUrlExceptions(newUrl);
          request = request.clone({
            url: newUrl,
          });
          isOnlineRequired = true;
        }
      }
    }

    if (environment.isLocalWeb && tokenKey && !isUrlWhileListed && !isOnlineRequired) {
      const newUrl = this.handleLocalUrlExceptions(request.url);
      request = request.clone({
        url: newUrl,
      });
    }

    //Fix url for api calls
    if (environment.isLocalWeb) {
      const newUrl = this.fixApiUrl(request.url);
      request = request.clone({
        url: newUrl,
      });
    }

    return next.handle(request);
  }

  private fixApiUrl(url: string): string {
    return url.replace('api/api/', 'api/').replace('api//api/', 'api/').replace('api/api/', 'api/');
  }

  buildSanitizedRegex(exception: string): RegExp {
    const sanitizedException = exception.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace('*', '(.*?)');
    return new RegExp(sanitizedException, 'g');
  }

  private handleUrlExceptions(url: string): string {
    const excepts: string[] = environment.urisExceptions.split(',');
    for (const exeppair of excepts) {
      const pair = exeppair.split('|');
      const exception = pair[0];
      const replacement = pair[1];
      const exceptionRegex = this.buildSanitizedRegex(exception);
      const newUrl = url.replace(exceptionRegex, replacement);

      if (url !== newUrl) return newUrl;
    }

    return url;
  }

  private handleLocalUrlExceptions(url: string): string {
    if (environment.urisLocalExceptions.trim().length <= 0) return url;

    const excepts: string[] = environment.urisLocalExceptions.split(',');
    for (const exeppair of excepts) {
      const pair = exeppair.split('|');
      const exception = pair[0];
      const replacement = pair[1];
      const exceptionRegex = this.buildSanitizedRegex(exception);
      const newUrl = url.replace(exceptionRegex, replacement);

      if (url !== newUrl) return newUrl;
    }

    return url;
  }

  private isRequiredConnectionOnlineByComponent(): boolean {
    const urls: string[] = environment.componentsOnline.split(',');
    const isOnlineComponentRoutes = urls.some((url) => this._router.url.includes(url));
    const currentComponentNonRouteOnline = sessionStorage.getItem('currentComponentNonRouteOnline') || '';
    const componentsNonRoute: string[] = environment.componentsNonRouteOnline.split(',');
    const isOnlineComponentNonRoutes = componentsNonRoute.some(
      (componentNonRoute) => componentNonRoute === currentComponentNonRouteOnline,
    );
    return isOnlineComponentRoutes || isOnlineComponentNonRoutes;
  }
}

export const ONLINE_INTERCEPTOR: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: OnlineInterceptor,
  multi: true,
};
